import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Trophy, TrendingUp, DollarSign, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CreditScorecard = () => {
  const [workshops, setWorkshops] = useState([]);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [tierSummary, setTierSummary] = useState(null);

  // Form state for new assessment
  const [formData, setFormData] = useState({
    workshop_id: '',
    workshop_name: '',
    revenue_history: '',
    average_dso: '',
    average_charger_uptime: '',
    first_pass_yield: '',
    warranty_claim_rate: '',
    tenure_months: '',
    average_monthly_revenue: '',
    // Notification settings
    notify_phone: '',
    notification_channel: 'sms'
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTierSummary();
    loadWorkshops();
  }, []);

  const loadTierSummary = async () => {
    try {
      const response = await fetch(`${API_URL}/api/credit-scoring/tiers/summary`);
      const data = await response.json();
      setTierSummary(data);
    } catch (err) {
      console.error('Failed to load tier summary:', err);
    }
  };

  const loadWorkshops = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/credit-scoring/financing/eligible?min_score=0`);
      const data = await response.json();
      setWorkshops(data.workshops || []);
    } catch (err) {
      console.error('Failed to load workshops:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCalculateScore = async (e) => {
    e.preventDefault();
    setCalculating(true);
    setError(null);
    setResult(null);

    try {
      // Parse revenue history (comma-separated)
      const revenueHistory = formData.revenue_history
        .split(',')
        .map(v => parseFloat(v.trim()))
        .filter(v => !isNaN(v));

      if (revenueHistory.length < 3) {
        throw new Error('Please provide at least 3 months of revenue history (comma-separated)');
      }

      const payload = {
        workshop_id: formData.workshop_id,
        workshop_name: formData.workshop_name,
        revenue_history: revenueHistory,
        payment_history: [],
        average_dso: parseFloat(formData.average_dso),
        average_charger_uptime: parseFloat(formData.average_charger_uptime),
        first_pass_yield: parseFloat(formData.first_pass_yield),
        warranty_claim_rate: parseFloat(formData.warranty_claim_rate),
        tenure_months: parseInt(formData.tenure_months),
        average_monthly_revenue: parseFloat(formData.average_monthly_revenue),
        // Include notification settings if phone provided
        ...(formData.notify_phone && {
          notify_phone: formData.notify_phone,
          notification_channel: formData.notification_channel
        })
      };

      const response = await fetch(`${API_URL}/api/credit-scoring/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Calculation failed');
      }

      setResult(data);
      loadTierSummary();
      loadWorkshops();
    } catch (err) {
      setError(err.message);
    } finally {
      setCalculating(false);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      'GOLD': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'SILVER': 'bg-gray-100 text-gray-800 border-gray-300',
      'BRONZE': 'bg-orange-100 text-orange-800 border-orange-300',
      'NOT_ELIGIBLE': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[tier] || 'bg-gray-100 text-gray-800';
  };

  const getTierIcon = (tier) => {
    if (tier === 'GOLD') return <Trophy className="w-5 h-5 text-yellow-600" />;
    if (tier === 'SILVER') return <Trophy className="w-5 h-5 text-gray-600" />;
    if (tier === 'BRONZE') return <Trophy className="w-5 h-5 text-orange-600" />;
    return <XCircle className="w-5 h-5 text-red-600" />;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">CapEx Credit Scorecard</h1>
          <p className="text-gray-600 mt-1">Workshop financing eligibility assessment</p>
        </div>
      </div>

      {/* Tier Summary Cards */}
      {tierSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-800">Gold Tier</p>
                  <p className="text-2xl font-bold text-yellow-900">
                    {tierSummary.tiers.find(t => t._id === 'GOLD')?.count || 0}
                  </p>
                </div>
                <Trophy className="w-8 h-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800">Silver Tier</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tierSummary.tiers.find(t => t._id === 'SILVER')?.count || 0}
                  </p>
                </div>
                <Trophy className="w-8 h-8 text-gray-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-800">Bronze Tier</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {tierSummary.tiers.find(t => t._id === 'BRONZE')?.count || 0}
                  </p>
                </div>
                <Trophy className="w-8 h-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800">Total Capacity</p>
                  <p className="text-2xl font-bold text-green-900">
                    ₹{(tierSummary.total_financing_capacity / 10000000).toFixed(1)}Cr
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: New Assessment Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              New Credit Assessment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCalculateScore} className="space-y-4">
              <div>
                <Label htmlFor="workshop_id">Workshop ID *</Label>
                <Input
                  id="workshop_id"
                  name="workshop_id"
                  placeholder="WS001"
                  value={formData.workshop_id}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="workshop_name">Workshop Name *</Label>
                <Input
                  id="workshop_name"
                  name="workshop_name"
                  placeholder="Delhi Hub Workshop"
                  value={formData.workshop_name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="revenue_history">Revenue History (Last 6-12 months) *</Label>
                <Input
                  id="revenue_history"
                  name="revenue_history"
                  placeholder="500000, 520000, 510000, 530000, 540000, 550000"
                  value={formData.revenue_history}
                  onChange={handleInputChange}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Comma-separated monthly revenue in ₹</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="average_dso">Avg DSO (days) *</Label>
                  <Input
                    id="average_dso"
                    name="average_dso"
                    type="number"
                    placeholder="25"
                    value={formData.average_dso}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="average_charger_uptime">Uptime % *</Label>
                  <Input
                    id="average_charger_uptime"
                    name="average_charger_uptime"
                    type="number"
                    step="0.1"
                    placeholder="95"
                    value={formData.average_charger_uptime}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_pass_yield">First Pass Yield % *</Label>
                  <Input
                    id="first_pass_yield"
                    name="first_pass_yield"
                    type="number"
                    step="0.1"
                    placeholder="92"
                    value={formData.first_pass_yield}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="warranty_claim_rate">Warranty Claims/100 *</Label>
                  <Input
                    id="warranty_claim_rate"
                    name="warranty_claim_rate"
                    type="number"
                    step="0.1"
                    placeholder="3"
                    value={formData.warranty_claim_rate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tenure_months">Tenure (months) *</Label>
                  <Input
                    id="tenure_months"
                    name="tenure_months"
                    type="number"
                    placeholder="18"
                    value={formData.tenure_months}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="average_monthly_revenue">Avg Monthly Revenue *</Label>
                  <Input
                    id="average_monthly_revenue"
                    name="average_monthly_revenue"
                    type="number"
                    placeholder="525000"
                    value={formData.average_monthly_revenue}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {/* Notification Settings */}
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">📱 Notification Settings (Optional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="notify_phone">Phone Number</Label>
                    <Input
                      id="notify_phone"
                      name="notify_phone"
                      type="tel"
                      placeholder="+91 98765 43210"
                      value={formData.notify_phone}
                      onChange={handleInputChange}
                    />
                    <p className="text-xs text-gray-500 mt-1">Receive credit score via SMS/WhatsApp</p>
                  </div>
                  <div>
                    <Label htmlFor="notification_channel">Channel</Label>
                    <select
                      id="notification_channel"
                      name="notification_channel"
                      value={formData.notification_channel}
                      onChange={handleInputChange}
                      className="w-full h-10 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="sms">SMS</option>
                      <option value="whatsapp">WhatsApp</option>
                    </select>
                  </div>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={calculating}
              >
                {calculating ? 'Calculating...' : 'Calculate Credit Score'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Right: Result Display */}
        <div className="space-y-6">
          {result && (
            <>
              {/* Score Card */}
              <Card className="border-2">
                <CardHeader className="pb-4">
                  <CardTitle>Credit Assessment Result</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 mb-4">
                      <span className={`text-5xl font-bold ${getScoreColor(result.credit_score)}`}>
                        {result.credit_score}
                      </span>
                    </div>
                    
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 ${getTierColor(result.tier)}`}>
                      {getTierIcon(result.tier)}
                      <span className="font-bold">{result.tier.replace('_', ' ')}</span>
                    </div>

                    <div className="mt-6 pt-6 border-t">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Financing Capacity</span>
                        <span className="text-2xl font-bold text-green-600">
                          ₹{(result.financing_capacity / 100000).toFixed(1)}L
                        </span>
                      </div>
                    </div>

                    {/* Notification Status */}
                    {result.notification && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center gap-2">
                          {result.notification.sent ? (
                            <>
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <span className="text-sm text-green-600">
                                {result.notification.mocked ? 'Notification queued (mock mode)' : 'Notification sent'} via {result.notification.channel?.toUpperCase()}
                              </span>
                            </>
                          ) : result.notification.to ? (
                            <>
                              <AlertCircle className="w-5 h-5 text-orange-500" />
                              <span className="text-sm text-orange-600">
                                Notification not sent (Twilio trial - verify phone number)
                              </span>
                            </>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Metrics Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Metrics Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(result.metrics_breakdown).map(([key, value]) => (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <span className={`font-semibold ${getScoreColor(value)}`}>
                            {value}/100
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              value >= 80 ? 'bg-green-500' :
                              value >= 60 ? 'bg-blue-500' :
                              value >= 40 ? 'bg-orange-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${value}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Strengths & Improvements */}
              {(result.strengths.length > 0 || result.improvements.length > 0) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Analysis</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {result.strengths.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-green-700 flex items-center gap-2 mb-2">
                          <CheckCircle className="w-4 h-4" />
                          Strengths
                        </h4>
                        <ul className="space-y-1">
                          {result.strengths.map((strength, idx) => (
                            <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                              <span className="text-green-500 mt-0.5">✓</span>
                              {strength}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {result.improvements.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-orange-700 flex items-center gap-2 mb-2">
                          <AlertCircle className="w-4 h-4" />
                          Areas for Improvement
                        </h4>
                        <ul className="space-y-1">
                          {result.improvements.map((improvement, idx) => (
                            <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                              <span className="text-orange-500 mt-0.5">→</span>
                              {improvement}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Existing Workshops Table */}
      <Card>
        <CardHeader>
          <CardTitle>Workshop Credit Profiles</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading workshops...</div>
          ) : workshops.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No workshops found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Workshop</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">City</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Score</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Tier</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Financing Capacity</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Last Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {workshops.map((workshop) => (
                    <tr key={workshop.workshop_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {workshop.workshop_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{workshop.city}</td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-semibold ${getScoreColor(workshop.credit_score)}`}>
                          {workshop.credit_score}/100
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getTierColor(workshop.tier)}`}>
                          {getTierIcon(workshop.tier)}
                          {workshop.tier}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600">
                        ₹{(workshop.financing_capacity / 100000).toFixed(1)}L
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(workshop.last_updated).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CreditScorecard;
